import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import Icon from 'features/imint/Icon';
import { COLORS, FONT } from 'features/imint/theme';
import { clearSelection } from 'features/imint/slices/imintSlice';
import satsureLogo from 'public/Satsure_Transparent_Bg.png';

// The IMINT shell top bar — ported verbatim from the prototype TopNav. Shown
// (instead of the dashboard Navbar) for users in the exclusive IMINT shell.
// The "IMINT" button returns to the landing (clears any selected target).
// Clicking the profile (name + avatar) opens a small dropdown with Logout —
// same OIDC sign-out semantics as the dashboard ProfileMenu.
const ImintTopNav = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();
  const authUser = useSelector((s) => s.auth.user);
  const name = authUser
    ? `${authUser.first_name || ''} ${authUser.last_name || ''}`.trim() || 'Analyst User'
    : 'Analyst User';
  // Email comes from the OIDC ID-token claims (`email` scope is requested, so
  // it's always there) — the backend /api/me user carries no email field.
  const email = (auth.user && auth.user.profile && auth.user.profile.email) || (authUser && authUser.email) || '';

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top, right } fixed coords
  const profileRef = useRef(null);
  const menuRef = useRef(null); // the portaled dropdown — part of "inside" for outside-click

  // Close on outside click / Escape / resize (the menu is position:fixed, so a
  // resize would leave it floating at stale coordinates).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      const inProfile = profileRef.current && profileRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inProfile && !inMenu) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onResize = () => setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  const toggleMenu = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 10, right: window.innerWidth - rect.right });
    setMenuOpen((o) => !o);
  };

  const onGoHome = () => {
    dispatch(clearSelection());
    navigate('/imint');
  };

  const handleLogout = async () => {
    try {
      // signoutRedirect() clears the local OIDC user, then bounces the browser
      // to Zitadel's end_session_endpoint with id_token_hint, which kills the
      // server-side session before redirecting to post_logout_redirect_uri.
      await auth.signoutRedirect();
    } catch (err) {
      // If Zitadel's end-session call fails, at least clear local state so
      // the user isn't stuck "logged in" client-side.
      await auth.removeUser();
      window.location.replace('/login');
    }
  };

  return (
    <>
      <div style={{ height: 6, background: '#0D0D0D' }} />
      <div style={{ height: 56, background: '#fff', borderBottom: `1px solid ${COLORS.divider}`, display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between', flexShrink: 0, position: 'relative', zIndex: 20, WebkitFontSmoothing: 'auto', MozOsxFontSmoothing: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <img src={satsureLogo} alt="SatSure" style={{ height: 30, display: 'block' }} />
          <div style={{ width: 1, height: 24, background: COLORS.divider }} />
          <button onClick={onGoHome} title="Return to IMINT landing page" style={{
            fontFamily: FONT, fontSize: 14, fontWeight: 600, color: COLORS.textSec, background: 'none', border: 'none',
            cursor: 'pointer', padding: '4px 8px', borderRadius: 4, margin: '-4px -8px',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.hover; e.currentTarget.style.color = COLORS.primaryDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = COLORS.textSec; }}>
            IMINT
          </button>
        </div>
        <div ref={profileRef} style={{ position: 'relative' }}>
          <button
            onClick={toggleMenu}
            title="Account"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, background: menuOpen ? COLORS.hover : 'none', border: 'none',
              cursor: 'pointer', padding: '4px 8px', borderRadius: 6, margin: '-4px -8px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.hover; }}
            onMouseLeave={(e) => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: COLORS.text }}>{name}</span>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: COLORS.primary50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="user" size={16} color={COLORS.primaryDark} />
            </div>
          </button>

          {/* Portaled to <body> with a top-level z-index — inside the nav it
              would inherit the bar's z:20 stacking context and page overlays
              (tab views z40, report takeover z200, panels) could cover it. */}
          {menuOpen && menuPos && createPortal(
            <div ref={menuRef} style={{
              position: 'fixed', top: menuPos.top, right: menuPos.right, minWidth: 180, background: '#fff',
              border: `1px solid ${COLORS.divider}`, borderRadius: 6, boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
              padding: 6, zIndex: 99999, fontFamily: FONT,
            }}>
              <div style={{ padding: '8px 10px 10px', borderBottom: `1px solid ${COLORS.divider}`, marginBottom: 6 }}>
                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                {email && (
                  <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textSec, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
                )}
              </div>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', borderRadius: 4,
                  background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: COLORS.text,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.hover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon name="log-out" size={14} color={COLORS.textSec} />
                Logout
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>
    </>
  );
};

export default ImintTopNav;
